// frontend/src/pages/Clients/ClientDetail.jsx
// ✅ VERSIÓN CORREGIDA CON DATOS EXTRAÍDOS DEL PDF

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Progress,
  Alert,
} from 'reactstrap';
import { clientsAPI } from '../../services/api';
import { PERMISSIONS } from '../../utils/permissions';
import ProtectedComponent from '../../components/ProtectedComponent';
import { parseDate } from '../../utils/dates';

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Estado unificado
  const [data, setData] = useState({
    client: null,
    contacts: [],
    quotes: [],
    orders: [],
    notes: [],
    stats: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('1');

  // Modales
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', mobile: '',
    position: 'buyer', department: '', is_primary: false, notes: ''
  });

  // Notas del cliente
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  useEffect(() => {
    loadClientData();
  }, [id]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const [clientRes, contactsRes, quotesRes, ordersRes, notesRes, statsRes] = await Promise.all([
        clientsAPI.getById(id),
        clientsAPI.getContacts(id),
        clientsAPI.getQuotes(id),
        clientsAPI.getOrders(id),
        clientsAPI.getNotes(id),
        clientsAPI.getStats(id),
      ]);

      console.log('🔍 CLIENT DATA:', clientRes.data);
      console.log('🔍 QUOTES:', quotesRes.data);
      console.log('🔍 STATS:', statsRes.data);

      setData({
        client: clientRes.data,
        contacts: contactsRes.data.contacts || [],
        quotes: quotesRes.data.quotes || [],
        orders: ordersRes.data.orders || [],
        notes: notesRes.data.notes || [],
        stats: statsRes.data,
      });
      setEditForm(clientRes.data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('No se pudieron cargar los datos del cliente');
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleEditClient = async () => {
    try {
      await clientsAPI.patch(id, editForm);
      setEditModalOpen(false);
      loadClientData();
    } catch (err) {
      alert('Error al actualizar el cliente');
    }
  };

  const handleAddContact = async () => {
    try {
      await clientsAPI.addContact(id, contactForm);
      setContactModalOpen(false);
      setContactForm({
        name: '', email: '', phone: '', mobile: '',
        position: 'buyer', department: '', is_primary: false, notes: ''
      });
      loadClientData();
    } catch (err) {
      alert('Error al agregar el contacto');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('¿Eliminar este contacto?')) return;
    try {
      await clientsAPI.deleteContact(contactId);
      loadClientData();
    } catch (err) {
      alert('Error al eliminar el contacto');
    }
  };

  // Handlers de Notas
  const handleAddNote = async () => {
    const content = newNote.trim();
    if (!content) return;
    try {
      setSavingNote(true);
      await clientsAPI.addNote(id, { content });
      setNewNote('');
      await loadClientData();
    } catch (err) {
      alert('Error al agregar la nota');
    } finally {
      setSavingNote(false);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
  };

  const handleUpdateNote = async (noteId) => {
    const content = editingNoteContent.trim();
    if (!content) return;
    try {
      await clientsAPI.updateNote(id, noteId, { content });
      cancelEditNote();
      await loadClientData();
    } catch (err) {
      alert('Error al actualizar la nota');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    try {
      await clientsAPI.deleteNote(id, noteId);
      await loadClientData();
    } catch (err) {
      alert('Error al eliminar la nota');
    }
  };

  // Utilidades
  const formatCurrency = (val) =>
    `$${(val || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (date) =>
    date ? parseDate(date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

  const formatDateTime = (date) =>
    date ? parseDate(date).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';

  const getStatusBadge = (status, type) => {
    const badges = {
      quote: {
        pending: { color: 'warning', text: 'Pendiente', icon: 'ni-time-alarm' },
        accepted: { color: 'success', text: 'Aceptada', icon: 'ni-check-bold' },
        rejected: { color: 'danger', text: 'Rechazada', icon: 'ni-fat-remove' },
        archived: { color: 'secondary', text: 'Archivada', icon: 'ni-archive-2' },
      },
      order: {
        pending: { color: 'warning', text: 'Pendiente', icon: 'ni-time-alarm' },
        confirmed: { color: 'info', text: 'Confirmada', icon: 'ni-check-bold' },
        invoiced: { color: 'primary', text: 'Facturada', icon: 'ni-single-copy-04' },
        completed: { color: 'success', text: 'Completada', icon: 'ni-like-2' },
      }
    };
    return badges[type]?.[status] || { color: 'secondary', text: status, icon: 'ni-tag' };
  };

  // Loading
  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando información del cliente...</p>
          </div>
        </Container>
      </>
    );
  }

  // Error
  if (error || !data.client) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="alert alert-danger shadow mt-5" role="alert">
            <span className="alert-icon"><i className="ni ni-fat-remove"></i></span>
            <span className="alert-text">{error || 'Cliente no encontrado'}</span>
            <button className="btn btn-sm btn-outline-danger ms-3" onClick={() => navigate('/admin/clients')}>
              <i className="ni ni-bold-left me-2"></i>Volver
            </button>
          </div>
        </Container>
      </>
    );
  }

  const { client, contacts, quotes, orders, notes, stats } = data;
  
  // ✅ NUEVO: Extraer datos de la última cotización
  const lastQuoteData = client.last_quote_data;
  const clientInfo = lastQuoteData?.client_info || {};
  const serviceDetails = lastQuoteData?.service_details || {};

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* ============================================ */}
        {/* HEADER */}
        {/* ============================================ */}
        <Row className="mb-4">
          <Col md="8">
            <div className="d-flex align-items-center">
              <div className="icon icon-shape icon-xl bg-white shadow text-center rounded-circle me-3">
                <span className="text-primary h1 mb-0 font-weight-bold">
                  {client.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h4 className="text-white mb-0">{client.name}</h4>
                <p className="text-white text-sm opacity-8 mb-0">
                  <i className="ni ni-badge me-1"></i>
                  ID: {client.client_id}
                  {client.rfc && (
                    <span className="ms-3">
                      <i className="ni ni-single-copy-04 me-1"></i>
                      RFC: {client.rfc}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </Col>
          <Col md="4" className="text-end">
            <Button color="white" size="sm" className="me-2" onClick={() => navigate('/admin/clients')}>
              <i className="ni ni-bold-left me-2"></i>Volver
            </Button>
            <ProtectedComponent permission={PERMISSIONS.EDIT_CLIENTS}>
              <Button color="white" size="sm" onClick={() => setEditModalOpen(true)}>
                <i className="ni ni-settings-gear-65 me-2"></i>Editar
              </Button>
            </ProtectedComponent>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* CARDS DE ESTADÍSTICAS */}
        {/* ============================================ */}
        <Row>
          {/* Card 1: Cotizaciones */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Propuestas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats?.quotes?.total_count || 0}
                      </h5>
                      <p className="mb-0">
                        <span className="text-info text-sm font-weight-bolder">
                          {formatCurrency(stats?.quotes?.subtotal_amount || stats?.quotes?.total_amount)}
                        </span>
                        <span className="text-xs text-muted"> total</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                      <i className="ni ni-single-copy-04 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Órdenes */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Órdenes de Compra
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats?.orders?.total_count || 0}
                      </h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          {formatCurrency(stats?.orders?.subtotal_amount || stats?.orders?.total_amount)}
                        </span>
                        <span className="text-xs text-muted"> total</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-bag-17 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 3: Tasa de Conversión */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Tasa Conversión
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats?.conversion_rate || 0}%
                      </h5>
                      <p className="mb-0">
                        <span className="text-warning text-sm font-weight-bolder">
                          {stats?.orders?.total_count || 0} / {stats?.quotes?.total_count || 0}
                        </span>
                        <span className="text-xs text-muted"> órdenes</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-chart-pie-35 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 4: Ticket Promedio */}
          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Ticket Promedio
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {formatCurrency(stats?.orders?.avg_amount)}
                      </h5>
                      <p className="mb-0">
                        <span className="text-primary text-sm font-weight-bolder">
                          Por orden
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-money-coins text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* INFORMACIÓN Y PROGRESO */}
        {/* ============================================ */}
        <Row className="mt-4">
          {/* Información del Cliente */}
          <Col lg="7" className="mb-lg-0 mb-4">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 pt-3 bg-transparent border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-capitalize mb-0">
                      <i className="ni ni-circle-08 me-2"></i>
                      Información del Cliente
                    </h6>
                    <p className="text-sm mb-0 mt-1 text-muted">
                      Datos generales y de contacto
                    </p>
                  </div>
                  <Badge 
                    color="" 
                    className={`badge-sm bg-gradient-${client.status === 'active' ? 'success' : 'secondary'}`}
                  >
                    {client.status === 'active' ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody className="p-3">
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                        Razón Social
                      </p>
                      <p className="text-sm text-dark mb-0 font-weight-bold">
                        {/* ✅ PRIORIZAR datos del PDF si existen */}
                        {client.legal_name || clientInfo.legal_name || client.name}
                      </p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                        RFC
                      </p>
                      <p className="text-sm text-dark mb-0">{client.rfc || 'No registrado'}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                        Account Manager
                      </p>
                      <p className="text-sm text-dark mb-0">
                        <i className="ni ni-single-02 me-1 text-primary"></i>
                        {client.account_manager_display || 'Sin asignar'}
                      </p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                        Email
                      </p>
                      <p className="text-sm text-dark mb-0">
                        {client.email ? (
                          <a href={`mailto:${client.email}`} className="text-primary">
                            {client.email}
                          </a>
                        ) : clientInfo.contact_email ? (
                          <a href={`mailto:${clientInfo.contact_email}`} className="text-primary">
                            {clientInfo.contact_email}
                          </a>
                        ) : 'No registrado'}
                      </p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                        Teléfono
                      </p>
                      <p className="text-sm text-dark mb-0">{client.phone || 'No registrado'}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                        Dirección
                      </p>
                      <p className="text-sm text-dark mb-0">
                        {client.address || 'No registrada'}
                        {(client.city || client.state) && (
                          <span className="d-block text-xs text-muted">
                            {client.city}{client.city && client.state && ', '}{client.state}
                            {client.postal_code && ` CP ${client.postal_code}`}
                          </span>
                        )}
                      </p>
                    </div>
                  </Col>
                </Row>
                
                
                
                {client.notes && (
                  <div className="mt-3 p-3 bg-secondary bg-opacity-10 border-radius-lg">
                    <p className="text-xs text-uppercase text-muted font-weight-bold mb-1">
                      <i className="ni ni-align-left-2 me-1"></i>
                      Notas Internas
                    </p>
                    <p className="text-sm text-dark mb-0">{client.notes}</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Métricas de Rendimiento */}
          <Col lg="5">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 p-3 border-0">
                <h6 className="mb-0">
                  <i className="ni ni-chart-bar-32 me-2"></i>
                  Métricas de Rendimiento
                </h6>
                <p className="text-xs text-muted mb-0 mt-1">
                  Resumen de actividad con este cliente
                </p>
              </CardHeader>
              <CardBody className="p-3">
                {/* Cotizaciones */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-single-copy-04 me-1"></i>
                      Propuestas Únicas
                    </span>
                    <span className="text-xs font-weight-bold">
                      {stats?.quotes?.total_count || 0}
                      {stats?.quotes?.total_versions > stats?.quotes?.total_count && (
                        <span className="text-muted ms-1">
                          ({stats.quotes.total_versions} versiones)
                        </span>
                      )}
                    </span>
                  </div>
                  <Progress 
                    max="100" 
                    value={Math.min((stats?.quotes?.total_count || 0) * 10, 100)} 
                    className="progress-sm"
                    color="info"
                  />
                </div>

                {/* Órdenes */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-bag-17 me-1"></i>
                      Órdenes Recibidas
                    </span>
                    <span className="text-xs font-weight-bold">
                      {stats?.orders?.total_count || 0}
                    </span>
                  </div>
                  <Progress 
                    max="100" 
                    value={Math.min((stats?.orders?.total_count || 0) * 10, 100)} 
                    className="progress-sm"
                    color="success"
                  />
                </div>

                {/* Tasa de Conversión */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-chart-pie-35 me-1"></i>
                      Tasa de Conversión
                    </span>
                    <span className="text-xs font-weight-bold">
                      {stats?.conversion_rate || 0}%
                    </span>
                  </div>
                  <Progress 
                    max="100" 
                    value={stats?.conversion_rate || 0} 
                    className="progress-sm"
                    color={stats?.conversion_rate >= 50 ? 'success' : stats?.conversion_rate >= 25 ? 'warning' : 'danger'}
                  />
                </div>

                {/* Contactos */}
                <div className="mb-0">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-badge me-1"></i>
                      Contactos Registrados
                    </span>
                    <span className="text-xs font-weight-bold">
                      {contacts.length}
                    </span>
                  </div>
                  <Progress 
                    max="100" 
                    value={Math.min(contacts.length * 20, 100)} 
                    className="progress-sm"
                    color="primary"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TABS DE CONTENIDO */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0">
              <CardHeader className="pb-0 border-0">
                <Nav tabs className="nav-fill flex-column flex-sm-row">
                  {[
                    { id: '1', icon: 'badge', label: 'Contactos', count: contacts.length },
                    { id: '2', icon: 'single-copy-04', label: 'Cotizaciones', count: stats?.quotes?.total_count || 0 },
                    { id: '3', icon: 'bag-17', label: 'Órdenes', count: orders.length },
                    { id: '4', icon: 'chat-round', label: 'Notas', count: notes.length },
                  ].map(tab => (
                    <NavItem key={tab.id} className="flex-fill">
                      <NavLink
                        className={`mb-0 ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className={`ni ni-${tab.icon} me-2`}></i>
                        {tab.label}
                        <Badge color="primary" className="ms-2 badge-sm">
                          {tab.count}
                        </Badge>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                <TabContent activeTab={activeTab}>
                  {/* TAB 1: CONTACTOS */}
                  <TabPane tabId="1">
                    <div className="px-4 pt-3 pb-2 d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">Lista de Contactos</h6>
                        <p className="text-xs text-muted mb-0">
                          Personas de contacto en esta empresa
                        </p>
                      </div>
                      <ProtectedComponent permission={PERMISSIONS.EDIT_CLIENTS}>
                        <Button color="primary" size="sm" className="btn-icon shadow-sm" onClick={() => setContactModalOpen(true)}>
                          <span className="btn-inner--icon"><i className="ni ni-fat-add"></i></span>
                          <span className="btn-inner--text ms-1">Agregar</span>
                        </Button>
                      </ProtectedComponent>
                    </div>
                    
                    <div className="table-responsive p-0">
                      {contacts.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="ni ni-badge text-muted" style={{ fontSize: '3rem' }}></i>
                          <p className="text-xs text-secondary mb-0 mt-3">
                            No hay contactos registrados
                          </p>
                          {/* ✅ NUEVO: Sugerir crear contacto desde datos del PDF */}
                          {clientInfo.contact_name && (
                            <div className="mt-3">
                              <Alert color="info" className="d-inline-block">
                                <small>
                                  <i className="ni ni-bulb-61 me-1"></i>
                                  Se encontró contacto en la propuesta: <strong>{clientInfo.contact_name}</strong>
                                  {clientInfo.contact_email && ` (${clientInfo.contact_email})`}
                                </small>
                              </Alert>
                              <div className="mt-2">
                                <Button 
                                  color="primary" 
                                  size="sm"
                                  onClick={() => {
                                    setContactForm({
                                      ...contactForm,
                                      name: clientInfo.contact_name || '',
                                      email: clientInfo.contact_email || '',
                                      mobile: clientInfo.contact_phone || '', 
                                      is_primary: true,
                                    });
                                    setContactModalOpen(true);
                                  }}
                                >
                                  <i className="ni ni-fat-add me-1"></i>
                                  Agregar este contacto
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Table className="align-items-center mb-0" hover>
                          <thead>
                            <tr>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Contacto
                              </th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Email
                              </th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Teléfono
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Cargo
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Principal
                              </th>
                              <th className="text-secondary opacity-7"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {contacts.map((contact) => (
                              <tr key={contact.id}>
                                <td>
                                  <div className="d-flex px-2 py-1">
                                    <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                      <i className="ni ni-single-02 text-white text-xs"></i>
                                    </div>
                                    <div className="d-flex flex-column justify-content-center">
                                      <h6 className="mb-0 text-sm">{contact.name}</h6>
                                      {contact.department && (
                                        <p className="text-xs text-secondary mb-0">{contact.department}</p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <p className="text-sm mb-0">{contact.email || 'N/A'}</p>
                                </td>
                                <td>
                                  <p className="text-sm mb-0">{contact.phone || contact.mobile || 'N/A'}</p>
                                </td>
                                <td className="align-middle text-center">
                                  <Badge color="" className="badge-sm bg-gradient-info">
                                    {contact.position_display || contact.position}
                                  </Badge>
                                </td>
                                <td className="align-middle text-center">
                                  {contact.is_primary && (
                                    <i className="ni ni-check-bold text-success"></i>
                                  )}
                                </td>
                                <td className="align-middle text-end pe-4">
                                  <ProtectedComponent permission={PERMISSIONS.EDIT_CLIENTS}>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="btn-icon shadow-sm"
                                      onClick={() => handleDeleteContact(contact.id)}
                                    >
                                      <i className="ni ni-fat-remove"></i>
                                    </Button>
                                  </ProtectedComponent>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </TabPane>

                  {/* TAB 2: COTIZACIONES */}
                  <TabPane tabId="2">
                    <div className="px-4 pt-3 pb-2">
                      <h6 className="mb-0">Historial de Propuestas</h6>
                      <p className="text-xs text-muted mb-0">
                        Propuestas enviadas a este cliente (excluyendo versiones archivadas)
                      </p>
                    </div>
                    
                    <div className="table-responsive p-0">
                      {quotes.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="ni ni-single-copy-04 text-muted" style={{ fontSize: '3rem' }}></i>
                          <p className="text-xs text-secondary mb-0 mt-3">
                            No hay propuestas para este cliente
                          </p>
                        </div>
                      ) : (
                        <Table className="align-items-center mb-0" hover>
                          <thead>
                            <tr>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Propuesta
                              </th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Servicio
                              </th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Vendedor
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Monto
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Estado
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Fecha
                              </th>
                              <th className="text-secondary opacity-7"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotes.map((quote) => {
                              const badge = getStatusBadge(quote.status, 'quote');
                              // ✅ NUEVO: Extraer datos del servicio si existen
                              const quoteItems = quote.items || {};
                              const quoteService = quoteItems.service_details || {};
                              
                              return (
                                <tr key={quote.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/quotes/${quote.id}`)}>
                                  <td>
                                    <div className="d-flex px-2 py-1">
                                      <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                        <i className="ni ni-single-copy-04 text-white text-xs"></i>
                                      </div>
                                      <div className="d-flex flex-column justify-content-center">
                                        <h6 className="mb-0 text-sm">{quote.proposal_number}</h6>
                                        <p className="text-xs text-secondary mb-0">v{quote.version}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column">
                                      <span className="text-sm font-weight-bold">
                                        {quote.category || quoteService.category || 'N/A'}
                                      </span>
                                      <span className="text-xs text-muted">
                                        {quote.service_type || quoteService.service_type || ''}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <p className="text-sm mb-0">{quote.seller_name || quote.account_manager || 'N/A'}</p>
                                  </td>
                                  <td className="align-middle text-center">
                                    <span className="text-dark text-sm font-weight-bold">
                                      {formatCurrency(quote.subtotal)}
                                    </span>
                                  </td>
                                  <td className="align-middle text-center">
                                    <Badge color="" className={`badge-sm bg-gradient-${badge.color} shadow-sm`}>
                                      <i className={`${badge.icon} me-1`}></i>
                                      {badge.text}
                                    </Badge>
                                  </td>
                                  <td className="align-middle text-center">
                                    <span className="text-secondary text-xs font-weight-bold">
                                      {formatDate(quote.created_at)}
                                    </span>
                                  </td>
                                  <td className="align-middle text-end pe-4">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="btn-icon shadow-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/admin/quotes/${quote.id}`);
                                      }}
                                    >
                                      <i className="ni ni-bold-right"></i>
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </TabPane>

                  {/* TAB 3: ÓRDENES */}
                  <TabPane tabId="3">
                    <div className="px-4 pt-3 pb-2">
                      <h6 className="mb-0">Historial de Órdenes de Compra</h6>
                      <p className="text-xs text-muted mb-0">
                        Todas las órdenes recibidas de este cliente
                      </p>
                    </div>
                    
                    <div className="table-responsive p-0">
                      {orders.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="ni ni-bag-17 text-muted" style={{ fontSize: '3rem' }}></i>
                          <p className="text-xs text-secondary mb-0 mt-3">
                            No hay órdenes de compra para este cliente
                          </p>
                        </div>
                      ) : (
                        <Table className="align-items-center mb-0" hover>
                          <thead>
                            <tr>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Orden
                              </th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Propuesta
                              </th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Vendedor
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Monto
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Estado
                              </th>
                              <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Fecha
                              </th>
                              <th className="text-secondary opacity-7"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((order) => {
                              const badge = getStatusBadge(order.status, 'order');
                              return (
                                <tr key={order.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/orders/${order.id}`)}>
                                  <td>
                                    <div className="d-flex px-2 py-1">
                                      <div className="icon icon-shape icon-sm bg-gradient-success shadow text-center rounded-circle me-2">
                                        <i className="ni ni-box-2 text-white text-xs"></i>
                                      </div>
                                      <div className="d-flex flex-column justify-content-center">
                                        <h6 className="mb-0 text-sm">{order.order_number || order.po_number}</h6>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <p className="text-sm mb-0">
                                      {order.quote_proposal_number || order.quote?.proposal_number || 'N/A'}
                                    </p>
                                  </td>
                                  <td>
                                    <p className="text-sm mb-0">{order.seller_name || 'N/A'}</p>
                                  </td>
                                  <td className="align-middle text-center">
                                    <span className="text-dark text-sm font-weight-bold">
                                      {formatCurrency(order.subtotal)}
                                    </span>
                                  </td>
                                  <td className="align-middle text-center">
                                    <Badge color="" className={`badge-sm bg-gradient-${badge.color} shadow-sm`}>
                                      <i className={`${badge.icon} me-1`}></i>
                                      {badge.text}
                                    </Badge>
                                  </td>
                                  <td className="align-middle text-center">
                                    <span className="text-secondary text-xs font-weight-bold">
                                      {formatDate(order.created_at)}
                                    </span>
                                  </td>
                                  <td className="align-middle text-end pe-4">
                                    <Button
                                      color="info"
                                      size="sm"
                                      className="btn-icon shadow-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/admin/orders/${order.id}`);
                                      }}
                                    >
                                      <i className="ni ni-bold-right"></i>
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </TabPane>

                  {/* TAB 4: NOTAS */}
                  <TabPane tabId="4">
                    <div className="px-4 pt-3 pb-2">
                      <h6 className="mb-0">Notas del Cliente</h6>
                      <p className="text-xs text-muted mb-0">
                        Comentarios internos visibles para el equipo. Se registra la fecha y el autor.
                      </p>
                    </div>

                    <div className="px-4">
                      {/* Agregar nota */}
                      <ProtectedComponent permission={PERMISSIONS.EDIT_CLIENTS}>
                        <div className="mb-4">
                          <Input
                            type="textarea"
                            rows="3"
                            placeholder="Escribe una nota sobre este cliente..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                          />
                          <div className="text-end mt-2">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleAddNote}
                              disabled={savingNote || !newNote.trim()}
                            >
                              <i className="ni ni-fat-add me-1"></i>
                              {savingNote ? 'Guardando...' : 'Agregar Nota'}
                            </Button>
                          </div>
                        </div>
                      </ProtectedComponent>

                      {/* Lista de notas */}
                      {notes.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="ni ni-chat-round text-muted" style={{ fontSize: '3rem' }}></i>
                          <p className="text-xs text-secondary mb-0 mt-3">
                            No hay notas registradas para este cliente
                          </p>
                        </div>
                      ) : (
                        <div className="pb-3">
                          {notes.map((note) => {
                            const wasEdited =
                              note.updated_at &&
                              note.created_at &&
                              (new Date(note.updated_at) - new Date(note.created_at)) > 1000;
                            const isEditing = editingNoteId === note.id;

                            return (
                              <div
                                key={note.id}
                                className="p-3 mb-3 bg-secondary bg-opacity-10 border-radius-lg"
                              >
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="d-flex align-items-center">
                                    <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                      <i className="ni ni-single-02 text-white text-xs"></i>
                                    </div>
                                    <div>
                                      <h6 className="mb-0 text-sm">{note.created_by_name || 'Sistema'}</h6>
                                      <p className="text-xs text-muted mb-0">
                                        <i className="ni ni-time-alarm me-1"></i>
                                        {formatDateTime(note.created_at)}
                                        {wasEdited && (
                                          <span className="ms-1 fst-italic">(editada)</span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  {!isEditing && (
                                    <ProtectedComponent permission={PERMISSIONS.EDIT_CLIENTS}>
                                      <div className="text-end">
                                        <Button
                                          color="link"
                                          size="sm"
                                          className="text-primary p-1"
                                          onClick={() => startEditNote(note)}
                                        >
                                          <i className="ni ni-ruler-pencil"></i>
                                        </Button>
                                        <Button
                                          color="link"
                                          size="sm"
                                          className="text-danger p-1"
                                          onClick={() => handleDeleteNote(note.id)}
                                        >
                                          <i className="ni ni-fat-remove"></i>
                                        </Button>
                                      </div>
                                    </ProtectedComponent>
                                  )}
                                </div>

                                {isEditing ? (
                                  <div>
                                    <Input
                                      type="textarea"
                                      rows="3"
                                      value={editingNoteContent}
                                      onChange={(e) => setEditingNoteContent(e.target.value)}
                                    />
                                    <div className="text-end mt-2">
                                      <Button color="secondary" size="sm" className="me-2" onClick={cancelEditNote}>
                                        Cancelar
                                      </Button>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleUpdateNote(note.id)}
                                        disabled={!editingNoteContent.trim()}
                                      >
                                        <i className="ni ni-check-bold me-1"></i>
                                        Guardar
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-dark mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                    {note.content}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ============================================ */}
      {/* MODAL: EDITAR CLIENTE */}
      {/* ============================================ */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setEditModalOpen(false)}>
          <i className="ni ni-settings-gear-65 me-2"></i>
          Editar Cliente
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Nombre / Razón Social *</Label>
                <Input 
                  value={editForm.name || ''} 
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })} 
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">RFC</Label>
                <Input 
                  value={editForm.rfc || ''} 
                  onChange={e => setEditForm({ ...editForm, rfc: e.target.value })} 
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Email</Label>
                <Input 
                  type="email" 
                  value={editForm.email || ''} 
                  onChange={e => setEditForm({ ...editForm, email: e.target.value })} 
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Teléfono</Label>
                <Input 
                  value={editForm.phone || ''} 
                  onChange={e => setEditForm({ ...editForm, phone: e.target.value })} 
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label className="form-control-label text-xs">Dirección</Label>
            <Input 
              type="textarea" 
              rows="2" 
              value={editForm.address || ''} 
              onChange={e => setEditForm({ ...editForm, address: e.target.value })} 
            />
          </FormGroup>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label text-xs">Ciudad</Label>
                <Input 
                  value={editForm.city || ''} 
                  onChange={e => setEditForm({ ...editForm, city: e.target.value })} 
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label text-xs">Estado</Label>
                <Input 
                  value={editForm.state || ''} 
                  onChange={e => setEditForm({ ...editForm, state: e.target.value })} 
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label text-xs">Código Postal</Label>
                <Input 
                  value={editForm.postal_code || ''} 
                  onChange={e => setEditForm({ ...editForm, postal_code: e.target.value })} 
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Estado del Cliente</Label>
                <Input 
                  type="select" 
                  value={editForm.status || 'active'} 
                  onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label className="form-control-label text-xs">Notas Internas</Label>
            <Input 
              type="textarea" 
              rows="3" 
              value={editForm.notes || ''} 
              onChange={e => setEditForm({ ...editForm, notes: e.target.value })} 
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModalOpen(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleEditClient}>
            <i className="ni ni-check-bold me-2"></i>
            Guardar Cambios
          </Button>
        </ModalFooter>
      </Modal>

      {/* ============================================ */}
      {/* MODAL: AGREGAR CONTACTO */}
      {/* ============================================ */}
      <Modal isOpen={contactModalOpen} toggle={() => setContactModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setContactModalOpen(false)}>
          <i className="ni ni-fat-add me-2"></i>
          Agregar Contacto
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Nombre *</Label>
                <Input 
                  value={contactForm.name} 
                  onChange={e => setContactForm({ ...contactForm, name: e.target.value })} 
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Email</Label>
                <Input 
                  type="email" 
                  value={contactForm.email} 
                  onChange={e => setContactForm({ ...contactForm, email: e.target.value })} 
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Teléfono</Label>
                <Input 
                  value={contactForm.phone} 
                  onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} 
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Celular</Label>
                <Input 
                  value={contactForm.mobile} 
                  onChange={e => setContactForm({ ...contactForm, mobile: e.target.value })} 
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Cargo</Label>
                <Input 
                  type="select" 
                  value={contactForm.position} 
                  onChange={e => setContactForm({ ...contactForm, position: e.target.value })}
                >
                  <option value="buyer">Comprador</option>
                  <option value="director">Director</option>
                  <option value="manager">Gerente</option>
                  <option value="engineer">Ingeniero</option>
                  <option value="quality">Calidad</option>
                  <option value="purchasing">Compras</option>
                  <option value="accounts_payable">Cuentas por Pagar</option>
                  <option value="other">Otro</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label text-xs">Departamento</Label>
                <Input 
                  value={contactForm.department} 
                  onChange={e => setContactForm({ ...contactForm, department: e.target.value })} 
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup check className="mb-3">
            <Label check>
              <Input 
                type="checkbox" 
                checked={contactForm.is_primary} 
                onChange={e => setContactForm({ ...contactForm, is_primary: e.target.checked })} 
              />
              {' '}Marcar como contacto principal
            </Label>
          </FormGroup>
          <FormGroup>
            <Label className="form-control-label text-xs">Notas</Label>
            <Input 
              type="textarea" 
              rows="2" 
              value={contactForm.notes} 
              onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} 
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setContactModalOpen(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleAddContact}>
            <i className="ni ni-check-bold me-2"></i>
            Agregar Contacto
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ClientDetail;
